import { Component, ElementRef, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { NgxMaskPipe } from 'ngx-mask';
import { PHONE_NUMBER_MASK } from '../../constants';
import type { CustomerContactInfoDto } from '@api/models';
import { CustomerService } from '../../services/customer.service';
import { ScrollToService } from '../../services/scroll-to/scroll-to.service';
import { DeviceService } from '../../services/device-detector/device.service';
import { IconComponent } from '../icon/icon.component';
import { UpdateEmailModalComponent } from './components/update-email-modal/update-email-modal.component';
import { UpdatePhoneModalComponent } from './components/update-phone-modal/update-phone-modal.component';
import { ConfirmPhoneModalComponent } from '../confirm-phone-modal/confirm-phone-modal.component';
import { ConfirmEmailModalComponent } from '../confirm-email-modal/confirm-email-modal.component';
import { UpdateFullNameModalComponent } from './components/update-full-name-modal/update-full-name-modal.component';

@Component({
  selector: 'customer-info',
  templateUrl: './customer-info.component.html',
  styleUrls: ['./customer-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ReactiveFormsModule, TranslatePipe, NgxMaskPipe, IconComponent, MatTooltipModule],
})
export class CustomerInfoComponent implements OnInit {
  customerService = inject(CustomerService);
  private scrollToService = inject(ScrollToService);
  private elementRef = inject(ElementRef);
  private dialog = inject(MatDialog);
  private deviceService = inject(DeviceService);

  source = input<string>('');
  changed = output<void>();

  isErrorsVisible = signal<boolean>(false);

  ngOnInit(): void {
  }

  checkValidity(): boolean {
    this.resetErrors();
    const isValid: boolean = this.isValid();

    if (!isValid) {
      this.isErrorsVisible.set(true);
      this.scrollToService.scrollTo({ target: this.elementRef, offset: -50 });
    }

    return isValid;
  }

  isValid(): boolean {
    let hasContactInfo = !!this.customerService.customer()?.contactInfo.phoneNumber
      && !!this.customerService.customer()?.contactInfo.lastName
      && !!this.customerService.customer()?.contactInfo.firstName;
    if (this.customerService.customer().contactInfo.isPhoneNumberNotSupported) {
      hasContactInfo = hasContactInfo && !!this.customerService.customer().contactInfo.email;
    }

    return this.customerService.isLoggedIn() && hasContactInfo;
  }

  protected isControlInvalid(key: 'phoneNumber' | 'email' | 'name'): boolean {
    let isValid: boolean = false;
    switch (key) {
      case 'phoneNumber':
        isValid = !!this.customerService.customer().contactInfo.phoneNumber;
        break;
      case 'email':
        isValid = this.customerService.customer().contactInfo.isPhoneNumberNotSupported
          ? !!this.customerService.customer().contactInfo.email
          : true;
        break;
      case 'name':
        isValid = !!this.customerService.customer().contactInfo.lastName
          && !!this.customerService.customer().contactInfo.firstName;
        break;
    }

    return this.isErrorsVisible() && !isValid;
  }

  private resetErrors(): void {
    this.isErrorsVisible.set(false);
    this.changed.emit();
  }

  onConfirmPhoneNumber(): void {
    this.dialog.open(ConfirmPhoneModalComponent);
  }

  onPhoneNumberInputClick(): void {
    if (this.deviceService.isDesktop() && this.customerService.customer().contactInfo.phoneNumber) {
      return;
    }
    this.onChangePhoneNumber();
  }

  onChangePhoneNumber(): void {
    this.dialog.open(UpdatePhoneModalComponent);
  }

  onConfirmEmail(): void {
    this.dialog.open(ConfirmEmailModalComponent);
  }

  onEmailInputClick(): void {
    if (this.deviceService.isDesktop() && this.customerService.customer().contactInfo.email) {
      return;
    }
    this.onChangeEmail();
  }

  onChangeEmail(): void {
    this.dialog.open(UpdateEmailModalComponent);
  }

  onNameInputClick(): void {
    const contactInfo = this.customerService.customer().contactInfo;
    if (this.deviceService.isDesktop() && contactInfo.lastName && contactInfo.firstName) {
      return;
    }
    this.onChangeName();
  }

  onChangeName(): void {
    this.dialog.open(UpdateFullNameModalComponent);
  }

  getValue(): CustomerContactInfoDto {
    return this.customerService.customer()?.contactInfo;
  }

  protected readonly PHONE_NUMBER_MASK = PHONE_NUMBER_MASK;
}
